/* eslint-disable react/no-danger */
import React, { lazy, Suspense } from 'react';
import dynamic from 'next/dynamic';
import { useTranslator } from 'tradera/lang/translate';
import Flag from 'tradera/components/flag/flag';
import { OptionsDisplay } from './options-display';
import { TakeawayOptionsDisplay } from './takeaway-options-display';
import MoreInfoFoldOut from '../more-info-fold-out';
import ApplicationErrorBoundary from 'tradera/components/application-error-boundary/application-error-boundary';
import InfoSection from 'tradera/apps/tradera/components/vip-shared/info-section';
import { CombinedShippingBadgeLink } from 'tradera/components/combined-shipping-badge/combined-shipping-badge-link';
import { mapCountryCodeToCountryKey } from 'tradera/localization/countries';
import { isInternationalShippingToCountryAllowed } from 'tradera/utils/allowed-buyer-region';
import { isNextJs } from 'tradera/utils/nextjs';
import { isSwedenCountryCodeOrUndefined } from 'tradera/localization/countrycode';
import { translateBuyerRegionText } from './translate-buyer-region-text';
import shippingProviders from 'tradera/apps/my-tradera/app/data/shipping-providers';
import SHIPPING_OPTIONS from 'tradera/components/shipping-product/shipping-options';
import styles from './shipping-options.module.scss';
import { useLocalizedPriceFormatter } from 'tradera/localization/use-localized-price-formatter';
import { selectMemberId } from 'tradera/state/member/selectors';
import { useSelector } from 'react-redux';
import {
    selectCurrentDescriptionLanguageCodeIso2,
    selectTranslatedDescription
} from 'tradera/state/views/view-item/selectors';
import { areLanguageCodesIso2Equal } from 'tradera/localization/languagecode';

const LazyLoadedMap = lazy(() => import('../map/map'));
const Map = isNextJs
    ? dynamic(() => import('../map/map'), {
          ssr: false,
          loading: () => (
              <div
                  className={`${styles.leaftletPlaceholder} animation-loading-shimmer border`}
              />
          )
      })
    : (props) => (
          <Suspense fallback={null}>
              <LazyLoadedMap {...props} />
          </Suspense>
      );

export const getDisplayText = (option, t, localizedPriceFormatter) => {
    const providerDisplayName =
        shippingProviders.map(t)[option.provider]?.text || option.provider;

    if (option.isTakeaway || option.isNotSpecified) {
        return providerDisplayName;
    } else if (option.cost === 0) {
        return t('shippingInfo_providerWithFreeShipping', {
            provider: providerDisplayName
        });
    } else {
        return localizedPriceFormatter
            ? `${localizedPriceFormatter(option.cost)} ${providerDisplayName}`
            : providerDisplayName;
    }
};

const getTooltipText = (option, t) => {
    if (option.tooltipText) {
        // TODO: this can be removed after move to Next.js
        return option.tooltipText;
    }
    if (option.isNotSpecified) {
        return t('vip_shipping_notSpecifiedDesc');
    } else if (option.provider === SHIPPING_OPTIONS.ALTERNATIVE) {
        return t('vip_shipping_altDesc');
    } else {
        return null;
    }
};

const getMapUrl = (city, zipCode, countryCodeIso2) => {
    const q = `${city}, ${zipCode}, ${countryCodeIso2}`;
    return `http://maps.apple.com/?q=${encodeURIComponent(q)}`;
};

export const ShippingOptions = ({
    allowedBuyerRegion,
    buyerCountryCode,
    shippingOptions,
    shippingTerms,
    seller,
    combinedShippingDays,
    freeShippingCostLimit
}) => {
    const { t } = useTranslator();
    const localizedPriceFormatter = useLocalizedPriceFormatter();
    const memberId = useSelector(selectMemberId);
    const translatedDescription = useSelector(selectTranslatedDescription);
    const currentDescriptionLanguageCodeIso2 = useSelector(
        selectCurrentDescriptionLanguageCodeIso2
    );

    const currentShippingTerms =
        translatedDescription &&
        areLanguageCodesIso2Equal(
            translatedDescription.languageCodeIso2,
            currentDescriptionLanguageCodeIso2
        )
            ? translatedDescription?.shippingTerms
            : shippingTerms;

    const sellerCity = seller.city;
    const sellerZipCode = seller.zipCode || seller.postcode?.code;
    const sellerCountryCodeIso2 = seller.countryCodeIso2;
    const sellerMapUrl =
        sellerCity && sellerZipCode && sellerCountryCodeIso2
            ? getMapUrl(sellerCity, sellerZipCode, sellerCountryCodeIso2)
            : null;

    const augmentOption = (option) => {
        const displayText = getDisplayText(option, t, localizedPriceFormatter);
        const tooltipText = getTooltipText(option, t);
        const location = option.isTakeaway
            ? {
                  city: sellerCity,
                  country: t(mapCountryCodeToCountryKey(sellerCountryCodeIso2)),
                  mapUrl: sellerMapUrl
              }
            : null;

        return {
            ...option,
            displayText: displayText,
            tooltipText: tooltipText,
            location: location
        };
    };

    const augmentedOptions = shippingOptions.map((option) =>
        augmentOption(option)
    );

    const sortOptionsByPriceAscending = (options) =>
        options.sort((a, b) => a - b);
    const splitShippingOptionsIntoPrimaryAndSecondaryAndTakeAwayOptions = (
        options
    ) => {
        const primaryOptions = [];
        const secondaryOptions = [];
        const takeAwayOptions = [];

        for (const option of options) {
            if (option.isTakeaway) {
                takeAwayOptions.push(option);
            } else if (option.toCountryCodeIso2 === buyerCountryCode) {
                primaryOptions.push(option);
            } else {
                secondaryOptions.push(option);
            }
        }
        return {
            primaryOptions,
            secondaryOptions,
            takeAwayOptions
        };
    };

    const { primaryOptions, secondaryOptions, takeAwayOptions } =
        splitShippingOptionsIntoPrimaryAndSecondaryAndTakeAwayOptions(
            augmentedOptions
        );

    sortOptionsByPriceAscending(primaryOptions);
    sortOptionsByPriceAscending(secondaryOptions);

    const shippingOptionFromCountryCode =
        augmentedOptions[0].fromCountryCodeIso2;
    const shippingOptionToCountryCode = augmentedOptions[0].toCountryCodeIso2;
    const shouldShowFromCountry =
        !isSwedenCountryCodeOrUndefined(shippingOptionFromCountryCode) &&
        shippingOptionFromCountryCode !== shippingOptionToCountryCode;

    const inAllowedBuyerRegion = isInternationalShippingToCountryAllowed(
        allowedBuyerRegion,
        buyerCountryCode
    );
    const showMoreInfoFoldOut =
        secondaryOptions.length > 0 || currentShippingTerms;
    const hasTakeawayOption = takeAwayOptions && takeAwayOptions.length > 0;
    const hasOnlyTakeawayOption =
        hasTakeawayOption &&
        primaryOptions.length === 0 &&
        secondaryOptions.length === 0;
    const hasOnlyNotSpecified =
        primaryOptions.length === 1 && primaryOptions[0].isNotSpecified;

    const hasMap = ({ countryCodeIso2, postcode }) => {
        if (!isSwedenCountryCodeOrUndefined(countryCodeIso2)) {
            return false;
        }
        const location = postcode && postcode.location;
        return postcode && location && location.latitude && location.longitude;
    };

    const buyerRegionText = translateBuyerRegionText(
        t,
        allowedBuyerRegion,
        buyerCountryCode,
        sellerCountryCodeIso2,
        hasOnlyTakeawayOption ||
            hasOnlyNotSpecified ||
            seller.memberId === memberId
    );
    const fromCountryText = shouldShowFromCountry ? (
        <>
            {' '}
            {t('vip_from_country_text', {
                shippingOptionCountry: t(
                    mapCountryCodeToCountryKey(shippingOptionFromCountryCode)
                )
            })}{' '}
            <Flag
                className="icon icon-sm mr-1 align-middle"
                countryCodeIso2={shippingOptionFromCountryCode}
            />
        </>
    ) : (
        ''
    );

    const showFreeShippingLimit =
        buyerCountryCode === shippingOptionFromCountryCode &&
        !hasOnlyTakeawayOption &&
        freeShippingCostLimit &&
        !shippingOptions?.every((option) => option.cost === 0);

    return (
        <InfoSection
            title={
                hasOnlyTakeawayOption
                    ? t('vip_shipping_pickup_only')
                    : t('vip_shipping')
            }
            titleRightContentArea={
                <>
                    {combinedShippingDays > 0 && !hasOnlyTakeawayOption && (
                        <div className="ml-auto">
                            <CombinedShippingBadgeLink
                                days={combinedShippingDays}
                            />
                        </div>
                    )}
                </>
            }>
            <div className={styles.shippingOptions}>
                {showFreeShippingLimit && (
                    <p className="mb-1 bold">
                        {t('vip_shipping_freeShippingLimit', {
                            amount: freeShippingCostLimit
                        })}
                    </p>
                )}
                {!hasOnlyTakeawayOption && (
                    <p className="mb-1">
                        <span className={showFreeShippingLimit ? '' : 'bold'}>
                            {buyerRegionText}
                        </span>
                        {fromCountryText}
                    </p>
                )}
                {(inAllowedBuyerRegion || hasOnlyNotSpecified) && (
                    <OptionsDisplay
                        options={primaryOptions}
                        shouldShowFromCountry={shouldShowFromCountry}
                    />
                )}
                {!inAllowedBuyerRegion && (
                    <p className="mb-1">
                        {t('vip_object_not_shipped_to_country')}
                    </p>
                )}
                {showMoreInfoFoldOut && (
                    <MoreInfoFoldOut
                        triggerText={t('vip_moreInfo')}
                        infoText={
                            <>
                                <OptionsDisplay
                                    options={secondaryOptions}
                                    shouldShowFromCountry={
                                        shouldShowFromCountry
                                    }
                                />
                                {currentShippingTerms && (
                                    <div
                                        className="mt-1"
                                        dangerouslySetInnerHTML={{
                                            __html: currentShippingTerms
                                        }}
                                    />
                                )}
                            </>
                        }
                    />
                )}
                {hasTakeawayOption && (
                    <>
                        <TakeawayOptionsDisplay
                            options={takeAwayOptions}
                            onlyTakeaway={hasOnlyTakeawayOption}
                        />

                        {hasMap(seller) && (
                            <div className="mb-2">
                                <ApplicationErrorBoundary>
                                    <Map
                                        postcode={seller.postcode}
                                        mapLink={sellerMapUrl}
                                    />
                                </ApplicationErrorBoundary>
                            </div>
                        )}
                    </>
                )}
            </div>
        </InfoSection>
    );
};
